import React, { ButtonHTMLAttributes, forwardRef } from 'react';

import clsx from 'clsx';

import { CorePopoverProps, Popover } from 'DesignSystem2/Popover';
import Padding from 'DesignSystem2/Theme/Padding';
import { noop } from 'utils/noop';

import { Icon, IconName } from '../Icon';
import Text from '../Text';

export interface BasePillProps {
    selected?: boolean;
    variant?:
        | 'default'
        | 'transparent'
        | 'muted'
        | 'yes'
        | 'no'
        | 'sell'
        | 'brand'
        | 'positive'
        | 'negative'
        | 'orange'
        | 'red'
        | 'green'
        | 'green-transparent'
        | 'calendar'
        | 'calendar-live'
        | 'live-read';
    disabled?: boolean;
    roi?: boolean;
    onClick?: (event: React.MouseEvent<Element, MouseEvent>) => void;
    dataTestId?: string;
    popoverProps?: CorePopoverProps;
    buttonProps?: ButtonHTMLAttributes<HTMLButtonElement>;
    className?: string;
}

interface TextPillProps extends BasePillProps {
    customTextElement?: JSX.Element;
    text?: string;
    textMedium?: boolean;
    textSmall?: boolean;
    icon?: IconName;
    iconSize?: number;
    trailingIcon?: IconName;
}

interface IconPillProps extends BasePillProps {
    customTextElement?: never;
    text?: never;
    textMedium?: boolean;
    textSmall?: boolean;
    icon: IconName;
    iconSize?: number;
    trailingIcon?: never;
}

type PillProps = TextPillProps | IconPillProps;

const Pill = forwardRef(
    (
        {
            selected = false,
            customTextElement,
            text,
            textMedium,
            textSmall,
            icon,
            iconSize = 20,
            trailingIcon,
            disabled = false,
            roi = false,
            variant = 'default',
            onClick = noop,
            className,
            dataTestId,
            popoverProps,
            buttonProps,
            ...otherProps
        }: PillProps,
        ref: React.Ref<HTMLButtonElement>,
    ): JSX.Element => {
        const disabledClasses = {
            selected: 'text-text-inverse bg-fill-x20 min-h-4 min-w-5',
            unselected: 'text-text-x30 bg-fill-x50 min-h-4 min-w-5',
        };
        const enabledSelectedClasses = {
            default: 'text-text-inverse bg-fill-x10 hover:opacity-80',
            transparent: 'text-text-inverse bg-fill-x10 hover:opacity-80',
            muted: 'text-text-x10 bg-fill-x50 hover:opacity-80',
            yes: 'text-text-inverse bg-yes-x10 hover:opacity-80',
            no: 'text-text-inverse bg-no-x10 hover:opacity-80',
            sell: 'text-text-inverse bg-sell-x10 hover:opacity-80',
            brand: 'text-text-inverse bg-brand-primary hover:opacity-80',
            positive: 'text-text-inverse bg-green-x10 hover:opacity-80',
            negative: 'text-text-x20 bg-custom-transparent border border-solid border-text-x20 hover:bg-fill-x50',
            orange: 'text-orange-x10 bg-orange-x20 hover:opacity-80',
            red: 'text-text-inverse bg-red-x10 hover:opacity-80',
            green: 'text-green-x10 bg-green-x20 border border-solid border-green-x30 hover:opacity-80',
            'green-transparent':
                'text-green-x40 bg-custom-transparent focus-visible:bg-green-x30 focus-visible:text-green-x50 focus-visible:outline-none',
            calendar: 'text-green-x40 bg-green-x20 hover:opacity-80',
            'calendar-live': 'text-text-inverse bg-red-x10 hover:opacity-80',
            'live-read': 'text-red-x10 bg-red-x20 hover:opacity-80',
        };
        const enabledUnSelectedClasses = {
            default: 'text-text-x10 bg-fill-x50 hover:opacity-80',
            transparent: 'text-text-x10 bg-custom-transparent border border-solid border-fill-x40',
            muted: 'text-text-x30 bg-custom-transparent hover:bg-fill-x50',
            yes: 'text-yes-x10 bg-yes-x20 hover:opacity-80',
            no: 'text-no-x10 bg-no-x20 hover:opacity-80',
            sell: 'text-text-x10 bg-fill-x50 hover:opacity-80',
            brand: 'text-brand-secondary bg-brand-x50 hover:opacity-80',
            positive: 'text-text-inverse bg-green-x10 hover:opacity-80',
            negative: 'text-text-x20 bg-custom-transparent border border-solid border-text-x20 hover:bg-fill-x50',
            orange: 'text-orange-x10 bg-orange-x20 hover:opacity-80',
            red: 'text-red-x10 bg-red-x20 hover:opacity-80',
            green: 'text-text-x10 bg-custom-transparent border border-solid border-fill-x40 hover:bg-fill-x60',
            'green-transparent':
                'text-text-x20 bg-custom-transparent hover:bg-fill-x50 focus-visible:bg-fill-x50 focus-visible:text-text-x20 focus-visible:outline-none',
            calendar: 'text-green-x10 bg-surface-x10 border border-solid border-green-x10 hover:bg-fill-x60',
            'calendar-live': 'text-text-inverse bg-red-x10 hover:opacity-80',
            'live-read': 'text-red-x10 bg-surface-x10 border border-solid border-red-x20 hover:opacity-80',
        };
        const ChosenIcon = !!icon && Icon[icon];
        const iconElement = !!ChosenIcon && <ChosenIcon size={iconSize} />;
        const TrailingIcon = !!trailingIcon && Icon[trailingIcon];
        const TextComponent = textMedium ? Text.Emphasis.x30 : !textSmall ? Text.Emphasis.x20 : Text.Emphasis.x10;
        const textElement = !!customTextElement ? customTextElement : !!text && <TextComponent>{text}</TextComponent>;

        const selectedKey = selected ? 'selected' : 'unselected';
        const chosenClasses = clsx(
            `inline-flex w-[inherit] items-center h-6 pl-1.5 pr-1.5 pb-0 pt-0 rounded-x30 min-w-[24px] whitespace-nowrap box-border shrink-0 border border-solid border-custom-transparent ${
                textElement && iconElement ? '' : 'place-content-center'
            } ${
                !disabled &&
                'cursor-pointer transition-[opacity,background-color,transform] duration-83 active:scale-90 active:transition-[opacity,background-color,transform] active:duration-50 [&:not(:active)]:transition-[opacity,background-color,transform] [&:not(:active)]:duration-[83ms,83ms,167ms]'
            }`,
            disabled
                ? disabledClasses[selectedKey]
                : selected
                ? enabledSelectedClasses[variant]
                : enabledUnSelectedClasses[variant],
            className,
        );

        const content = (
            <>
                {iconElement}
                {iconElement && textElement && <Padding spacing={0.5} />}
                {roi && <Text.Body.x10>{'ROI'}</Text.Body.x10>}
                {roi && textElement && <Padding spacing={0.5} />}
                {textElement}
                {TrailingIcon && (
                    <>
                        <Padding spacing={0.5} />
                        <TrailingIcon size={'20px'} />
                    </>
                )}
            </>
        );

        // Pull out width so the wrapper matches child width (if defined)
        const {
            style: {
                backgroundColor = undefined,
                color = undefined,
                padding = undefined,
                border = undefined,
                borderRadius = undefined,
                borderWidth = undefined,
                height = undefined,
                boxShadow = undefined,
                ...restStyle
            } = {},
            ...restButtonProps
        } = buttonProps || {};

        const pill = (
            <div onClick={disabled ? undefined : onClick} className={'w-max'} style={{ ...restStyle, height }}>
                <button
                    disabled={disabled}
                    className={chosenClasses}
                    ref={ref}
                    type={'button'}
                    data-testid={dataTestId}
                    {...restButtonProps}
                    {...otherProps}
                    style={{
                        backgroundColor,
                        color,
                        padding,
                        border,
                        borderRadius,
                        borderWidth,
                        height,
                        boxShadow,
                        fontFamily: 'var(--inter-font)',
                    }}
                >
                    {content}
                </button>
            </div>
        );

        if (!!popoverProps) {
            return <Popover {...popoverProps}>{pill}</Popover>;
        }

        return pill;
    },
);

Pill.displayName = 'Pill';
export default Pill;
